import type { DocumentManifestDetailForReviewQuery } from "generated-graphql/graphql";

import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import { useDeleteDocumentMutation } from "hooks/documents";
import { useValidatingForm } from "hooks/useValidatingForm";
import { useCallback, useMemo, useState } from "react";
import { useFieldArray } from "react-hook-form";
import {
  DELETE_DOCUMENT_MANIFEST,
  MANIFEST_LIST_QUERIES,
} from "routes/Customer/Waste/Manifests/api";

import { isGeneratorMailingAddressPresent, isGeneratorSiteAddressBlank } from "../generatorAddress";
import { DOCUMENT_MANIFEST_DETAIL, EDIT_DOCUMENT_MANIFEST } from "../queries";
import { ManifestFormValues } from "../types";
import { useDocumentManifestReviewValidation } from "../validationHooks/useDocumentManifestReviewValidation";
import { NO_READING_PAGES, type ReadingPages } from "./useDocumentFollowing";

type LoadedManifestDetail = DocumentManifestDetailForReviewQuery["documentManifestDetail"];
type LoadedWasteLine = NonNullable<LoadedManifestDetail>["wasteLines"][number];
type WasteLineFormValues = ManifestFormValues["wasteLines"][number];

/** The form before any manifest has loaded, and the shape every field resets to. */
export const EMPTY_MANIFEST_FORM_VALUES: ManifestFormValues = {
  facilityId: "",
  manifestTrackingNumber: "",
  vendorSource: "",
  shippedDate: "",
  receivedDate: "",
  generatorId: "",
  generatorName: "",
  generatorMailStreet1: "",
  generatorMailStreet2: "",
  generatorMailCity: "",
  generatorMailState: "",
  generatorMailZip: "",
  generatorTxStateWasteRegistrationId: "",
  generatorSiteAddressSameAsMailing: null,
  generatorLocationStreet1: "",
  generatorLocationStreet2: "",
  generatorLocationCity: "",
  generatorLocationState: "",
  generatorLocationZip: "",
  desFacilityId: "",
  desFacilityName: "",
  desFacLocationStreet1: "",
  desFacLocationCity: "",
  desFacLocationState: "",
  desFacLocationZip: "",
  discrepancyComments: "",
  residueComments: "",
  rejectionComments: "",
  wasteLines: [],
  transporters: [],
};

function formatDateForInput(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toISOString().slice(0, 10);
  } catch {
    return "";
  }
}

/** Nullable server string → form string; RHF inputs want "" rather than null. */
function text(value: string | null | undefined): string {
  return value ?? "";
}

/** Loaded manifest detail → the form's initial values. */
function toManifestFormValues(detail: LoadedManifestDetail): ManifestFormValues {
  const wm = detail?.wasteManifest;
  if (!detail || !wm) return EMPTY_MANIFEST_FORM_VALUES;

  return {
    // Seed from the confirmed facility only. An unconfirmed AI match is a
    // suggestion (see `suggestedFacility`), surfaced as a hint the reviewer
    // must accept — never pre-filled, so a partial save can't silently promote
    // a weak match to a live facility link.
    facilityId: text(wm.facility?.id),
    manifestTrackingNumber: text(wm.manifestTrackingNumber),
    vendorSource: text(wm.vendorSource),
    shippedDate: formatDateForInput(wm.shippedDate),
    receivedDate: formatDateForInput(wm.receivedDate),
    generatorId: text(wm.generatorId),
    generatorName: text(wm.generatorName),
    generatorMailStreet1: text(wm.generatorMailStreet1),
    generatorMailStreet2: text(wm.generatorMailStreet2),
    generatorMailCity: text(wm.generatorMailCity),
    generatorMailState: text(wm.generatorMailState),
    generatorMailZip: text(wm.generatorMailZip),
    generatorTxStateWasteRegistrationId: text(wm.generatorTxStateWasteRegistrationId),
    generatorSiteAddressSameAsMailing: wm.generatorSiteAddressSameAsMailing ?? null,
    generatorLocationStreet1: text(wm.generatorLocationStreet1),
    generatorLocationStreet2: text(wm.generatorLocationStreet2),
    generatorLocationCity: text(wm.generatorLocationCity),
    generatorLocationState: text(wm.generatorLocationState),
    generatorLocationZip: text(wm.generatorLocationZip),
    desFacilityId: text(wm.desFacilityId),
    desFacilityName: text(wm.desFacilityName),
    desFacLocationStreet1: text(wm.desFacLocationStreet1),
    desFacLocationCity: text(wm.desFacLocationCity),
    desFacLocationState: text(wm.desFacLocationState),
    desFacLocationZip: text(wm.desFacLocationZip),
    discrepancyComments: text(wm.discrepancyComments),
    residueComments: text(wm.residueComments),
    rejectionComments: text(wm.rejectionComments),
    wasteLines: detail.wasteLines.map(toWasteLineFormValues),
    transporters: detail.wasteTransporters.map((t) => ({
      transporterLineNumber: t.transporterLineNumber,
      transporterId: text(t.transporterId),
      transporterName: text(t.transporterName),
    })),
  };
}

/** Server waste line → editable form row. Waste codes edit as comma lists. */
function toWasteLineFormValues(wl: LoadedWasteLine): WasteLineFormValues {
  return {
    wasteLineNumber: wl.wasteLineNumber,
    dotHazardous: wl.dotHazardous ?? false,
    dotIdNumberDescription: text(wl.dotIdNumberDescription),
    dotPrintedInformation: text(wl.dotPrintedInformation),
    nonHazWasteDescription: text(wl.nonHazWasteDescription),
    containerNumber: wl.containerNumber ?? null,
    containerTypeCode: text(wl.containerTypeCode),
    quantity: wl.quantity ?? null,
    qtyUnitOfMeasureCode: text(wl.qtyUnitOfMeasureCode),
    managementMethodCode: text(wl.managementMethodCode),
    federalWasteCodes: (wl.federalWasteCodes ?? []).join(", "),
    stateWasteCodes: (wl.stateWasteCodes ?? []).join(", "),
    tags: (wl.tags ?? []).map((tag) => tag.name),
  };
}

function splitCodeList(codes: string): string[] {
  if (!codes) return [];
  return codes
    .split(",")
    .map((code) => code.trim())
    .filter(Boolean);
}

/** Form row → `DocumentWasteLineEditInput`. */
function toWasteLineEditInput(wl: WasteLineFormValues) {
  return {
    wasteLineNumber: wl.wasteLineNumber,
    dotHazardous: wl.dotHazardous,
    dotIdNumberDescription: wl.dotIdNumberDescription || null,
    dotPrintedInformation: wl.dotPrintedInformation || null,
    nonHazWasteDescription: wl.nonHazWasteDescription || null,
    containerNumber: wl.containerNumber,
    containerTypeCode: wl.containerTypeCode || null,
    quantity: wl.quantity,
    qtyUnitOfMeasureCode: wl.qtyUnitOfMeasureCode || null,
    managementMethodCode: wl.managementMethodCode || null,
    federalWasteCodes: splitCodeList(wl.federalWasteCodes),
    stateWasteCodes: splitCodeList(wl.stateWasteCodes),
    // Always sent, so clearing the picker removes the line's tags rather than
    // reading as "leave them alone".
    tags: wl.tags ?? [],
  };
}

export function useManifestReviewSection({
  wasteManifestId: wasteManifestIdProp,
  documentId,
  onManifestChanged,
}: {
  /** Resolved waste manifest ID — from the dialog prop or the document's sourceWasteManifest */
  wasteManifestId?: string | null;
  /** Current document ID — needed for document deletion */
  documentId?: string;
  onManifestChanged?: () => void;
}) {
  const alerts = useAlerts();
  const client = useApolloClient();
  const [deleteDocumentMutation, { loading: deletingDocument }] = useDeleteDocumentMutation({
    refetchQueries: [...MANIFEST_LIST_QUERIES, "Documents", "FacilityDocuments"],
  });

  // editDocumentManifest regenerates the deterministic WasteManifest id when
  // the tracking number changes, but callers hold the id the dialog was opened
  // with (a prop or a cached grid row). Track the id returned by the latest
  // save and prefer it — otherwise every post-rename operation (detach, delete,
  // detail refetch) targets the dead id and fails with "Waste manifest not found".
  // The rename is keyed to the prop/document context it happened under, so
  // navigating to another document (multi-doc review) or an externally
  // re-resolved id discards it.
  const [renamedManifest, setRenamedManifest] = useState<{
    contextKey: string;
    id: string;
  } | null>(null);
  const manifestContextKey = `${wasteManifestIdProp ?? ""}:${documentId ?? ""}`;

  const wasteManifestId =
    renamedManifest?.contextKey === manifestContextKey
      ? renamedManifest.id
      : (wasteManifestIdProp ?? null);

  const { data: manifestDetailData, loading: manifestDetailLoading } = useQuery(
    DOCUMENT_MANIFEST_DETAIL,
    {
      variables: { wasteManifestId: wasteManifestId ?? "" },
      skip: !wasteManifestId,
      fetchPolicy: "cache-and-network",
    }
  );

  const [editManifestMutation, { loading: manifestSaving }] = useMutation(EDIT_DOCUMENT_MANIFEST, {
    // ProcessedManifestDocuments backs the drawer's Documents section — refetch
    // it too so an edit/confirm reflects there without a manual page refresh.
    // DocumentManifestDetailForReview is deliberately NOT refetched here: when a
    // save renames the manifest id, a by-name refetch re-runs the detail query
    // with the pre-rename variables and errors. saveManifest refetches it
    // explicitly (same id) or lets the id change trigger a fresh query (rename).
    refetchQueries: ["ProcessedManifests", "ProcessedManifestDocuments"],
  });

  const [deleteManifestMutation, { loading: deletingManifest }] = useMutation(
    DELETE_DOCUMENT_MANIFEST,
    { refetchQueries: ["ProcessedManifests"] }
  );

  const manifestFormValues = useMemo(
    () => toManifestFormValues(manifestDetailData?.documentManifestDetail),
    [manifestDetailData]
  );

  // On-change EPA-ID validation (format + registered/in-tenant lookups) through
  // the shared compliance-engine validator. Warn-and-allow: issues render inline
  // on the fields but are ERROR-level, so they never block saving.
  const validateManifestReview = useDocumentManifestReviewValidation(wasteManifestId);

  // Issues computed server-side on load, so EPA-ID warnings show on the parsed
  // values before any edit. Subsequent edits re-validate via validateManifestReview.
  const initialIssues = manifestDetailData?.documentManifestDetail?.issues;

  const manifestForm = useValidatingForm<ManifestFormValues>(
    manifestFormValues,
    initialIssues,
    validateManifestReview,
    undefined,
    // Validated on blur rather than on every character. This form is sixty
    // fields beside a document pane, and each validation pass reports through
    // several state updates — re-rendering all of it while the reviewer is
    // still typing the value being complained about. Blur is also when the
    // answer is worth having: a half-typed EPA ID is not yet wrong.
    { validateOn: "onBlur" }
  );
  const {
    control: manifestControl,
    handleSubmit: handleManifestSubmit,
    watch,
    getValues,
    setValue,
    issues: manifestIssues,
    formState: { isDirty: manifestIsDirty },
  } = manifestForm;

  const manifestNeedsReview = useMemo(
    () =>
      manifestDetailData?.documentManifestDetail?.wasteManifest?.sourceDocument?.documentMetadata
        ?.needsReview === true,
    [manifestDetailData]
  );

  // Confirmed facility (from WasteManifest.facilityId). Used to seed the facility
  // picker's DISPLAY value as a full fragment so it renders the facility name
  // immediately, rather than relying on the picker's id-hydration query (which
  // the waste-facility variant doesn't resolve for a bare id).
  const facility = useMemo(
    () => manifestDetailData?.documentManifestDetail?.wasteManifest?.facility ?? null,
    [manifestDetailData]
  );

  // Unconfirmed AI facility match + its provenance. Rendered as a hint the
  // reviewer can accept; not seeded into the form. Null once confirmed or when
  // no match was found.
  const suggestedFacility = useMemo(
    () => manifestDetailData?.documentManifestDetail?.suggestedFacility ?? null,
    [manifestDetailData]
  );
  const suggestedFacilitySource = useMemo(
    () => manifestDetailData?.documentManifestDetail?.suggestedFacilitySource ?? null,
    [manifestDetailData]
  );

  // Drives the site address "Same address" affordance (EPA Box 5): the site
  // address is left blank when it equals the mailing address. Derived from the
  // loaded data (not live form values) so the affordance doesn't disappear as
  // the reviewer types.
  const generatorSiteAddressInitiallyBlank = useMemo(
    () => isGeneratorSiteAddressBlank(manifestDetailData?.documentManifestDetail?.wasteManifest),
    [manifestDetailData]
  );
  const generatorMailingAddressPresent = useMemo(
    () =>
      isGeneratorMailingAddressPresent(manifestDetailData?.documentManifestDetail?.wasteManifest),
    [manifestDetailData]
  );

  const originalTrackingNumber =
    manifestDetailData?.documentManifestDetail?.wasteManifest?.manifestTrackingNumber ?? "";
  const currentTrackingNumber = watch("manifestTrackingNumber");
  const trackingNumberChanged =
    !!originalTrackingNumber &&
    !!currentTrackingNumber &&
    currentTrackingNumber !== originalTrackingNumber;

  // Narrowed to what the form actually uses, and held stable.
  //
  // `useFieldArray` rebuilds its result object on every render, so passing it
  // straight through would make this hook's return a new object every time and
  // put every section of the form permanently beyond memoization. The methods
  // on it are already stable; only `fields` changes, and only when rows do.
  const {
    fields: wasteLineFields,
    append: appendWasteLine,
    remove: removeWasteLine,
  } = useFieldArray({ control: manifestControl, name: "wasteLines" });
  const wasteLineFieldArray = useMemo(
    () => ({ fields: wasteLineFields, append: appendWasteLine, remove: removeWasteLine }),
    [wasteLineFields, appendWasteLine, removeWasteLine]
  );

  const {
    fields: transporterFields,
    append: appendTransporter,
    remove: removeTransporter,
  } = useFieldArray({ control: manifestControl, name: "transporters" });
  const transporterFieldArray = useMemo(
    () => ({ fields: transporterFields, append: appendTransporter, remove: removeTransporter }),
    [transporterFields, appendTransporter, removeTransporter]
  );

  const [deletedWasteLineNumbers, setDeletedWasteLineNumbers] = useState<string[]>([]);
  const [deletedTransporterLineNumbers, setDeletedTransporterLineNumbers] = useState<string[]>([]);

  // Save manifest form data. `markReviewed` decouples persisting edits from
  // confirming the document:
  //  - Confirm / Mark Reviewed passes `true` (clears needsReview on the source
  //    document), allowed even on an unedited doc.
  //  - Partial Save passes `false` (persists edits, keeps the doc in Needs
  //    Review). Only offered when the form is dirty, so it's a no-op otherwise.
  const saveManifest = useCallback(
    async ({ markReviewed = false }: { markReviewed?: boolean } = {}) => {
      if (!wasteManifestId || (!manifestIsDirty && !markReviewed)) return;

      return new Promise<void>((resolve, reject) => {
        handleManifestSubmit(async (values) => {
          try {
            const { data } = await editManifestMutation({
              variables: {
                wasteManifestId,
                input: {
                  // Empty string = "no facility" (clear); a value = confirmed
                  // facility. The resolver only mints/moves the FacilityDocument
                  // link on markReviewed.
                  facilityId: values.facilityId || null,
                  manifestTrackingNumber: values.manifestTrackingNumber || null,
                  vendorSource: values.vendorSource,
                  shippedDate: values.shippedDate || null,
                  receivedDate: values.receivedDate || null,
                  generatorId: values.generatorId || null,
                  generatorName: values.generatorName || null,
                  generatorMailStreet1: values.generatorMailStreet1 || null,
                  generatorMailStreet2: values.generatorMailStreet2 || null,
                  generatorMailCity: values.generatorMailCity || null,
                  generatorMailState: values.generatorMailState || null,
                  generatorMailZip: values.generatorMailZip || null,
                  generatorTxStateWasteRegistrationId:
                    values.generatorTxStateWasteRegistrationId || null,
                  generatorSiteAddressSameAsMailing: values.generatorSiteAddressSameAsMailing,
                  generatorLocationStreet1: values.generatorLocationStreet1 || null,
                  generatorLocationStreet2: values.generatorLocationStreet2 || null,
                  generatorLocationCity: values.generatorLocationCity || null,
                  generatorLocationState: values.generatorLocationState || null,
                  generatorLocationZip: values.generatorLocationZip || null,
                  desFacilityId: values.desFacilityId || null,
                  desFacilityName: values.desFacilityName || null,
                  desFacLocationStreet1: values.desFacLocationStreet1 || null,
                  desFacLocationCity: values.desFacLocationCity || null,
                  desFacLocationState: values.desFacLocationState || null,
                  desFacLocationZip: values.desFacLocationZip || null,
                  // Box 18a narratives sent verbatim — the resolver's
                  // blankToNull helper decides clear-vs-keep, so an empty
                  // string must reach it (don't `|| null`).
                  discrepancyComments: values.discrepancyComments,
                  residueComments: values.residueComments,
                  rejectionComments: values.rejectionComments,
                  wasteLines: values.wasteLines.map(toWasteLineEditInput),
                  transporters: values.transporters.map((t) => ({
                    transporterLineNumber: t.transporterLineNumber,
                    transporterId: t.transporterId || null,
                    transporterName: t.transporterName || null,
                  })),
                  deleteWasteLineNumbers: deletedWasteLineNumbers,
                  deleteTransporterLineNumbers: deletedTransporterLineNumbers,
                  markReviewed,
                },
              },
            });

            onManifestChanged?.();

            setDeletedWasteLineNumbers([]);
            setDeletedTransporterLineNumbers([]);
            // Refresh the manifest drawer (its Sources chips + "needs review"
            // banner) and every manifest list, so a confirm reflects without a
            // manual page reload no matter which surface opened the review.
            // The drawer reads via useSuspenseQuery, which the mutation's
            // string-name refetchQueries doesn't reliably re-run — so refetch
            // the query documents directly, as detach/delete already do.
            await client.refetchQueries({ include: MANIFEST_LIST_QUERIES });
            // A tracking-number change makes the server regenerate the
            // manifest's deterministic id. Adopt the returned id so later
            // operations don't target the dead one; the variable change also
            // re-runs the detail query. On a same-id save, refetch the detail
            // explicitly (see the mutation's refetchQueries comment).
            const savedId = data?.editDocumentManifest?.wasteManifest?.id;
            if (savedId && savedId !== wasteManifestId) {
              setRenamedManifest({ contextKey: manifestContextKey, id: savedId });
            } else {
              client.refetchQueries({ include: ["DocumentManifestDetailForReview"] });
            }
            resolve();
          } catch (err) {
            reject(err);
          }
        })();
      });
    },
    [
      wasteManifestId,
      manifestIsDirty,
      handleManifestSubmit,
      editManifestMutation,
      onManifestChanged,
      deletedWasteLineNumbers,
      deletedTransporterLineNumbers,
      manifestContextKey,
      client,
    ]
  );

  const tenantId = manifestDetailData?.documentManifestDetail?.tenantId ?? null;

  // Where in the PDF each part of the manifest was read from, so the document
  // pane can open on the manifest's own page rather than page 1 of a bundle
  // whose first page is a scale ticket. Keyed by printed line number, because
  // the form sorts rows and the reading does not.
  const readingPages: ReadingPages = useMemo(() => {
    const reading = manifestDetailData?.documentManifestDetail?.reading;
    if (!reading) return NO_READING_PAGES;
    const wasteLinePages = new Map<string, number>();
    for (const line of reading.wasteLines) {
      if (line.pageNumber != null) wasteLinePages.set(line.wasteLineNumber, line.pageNumber);
    }
    const transporterPages = new Map<string, number>();
    for (const transporter of reading.transporters) {
      if (transporter.pageNumber != null) {
        transporterPages.set(transporter.transporterLineNumber, transporter.pageNumber);
      }
    }
    return {
      manifestPageNumber: reading.manifestPageNumber ?? null,
      wasteLinePages,
      transporterPages,
    };
  }, [manifestDetailData]);

  // The model's own confidence per field. Manifests have never had this — every
  // value reached the form looking the same whether it was pre-printed and crisp
  // or pieced off a faxed smudge.
  const fieldConfidences = useMemo(
    () => manifestDetailData?.documentManifestDetail?.reading?.fieldConfidences ?? [],
    [manifestDetailData]
  );

  // The reading projected for offering back, already in the form's terms. The
  // server does that projection so the rule for what a reviewer reads versus what
  // the field stores lives in one place, next to the pillars' copy of it.
  const readingOffers = useMemo(
    () => manifestDetailData?.documentManifestDetail?.reading?.offers ?? [],
    [manifestDetailData]
  );

  const manifestLoading = manifestDetailLoading;
  const manifestDataLoaded = !!manifestDetailData?.documentManifestDetail && !manifestDetailLoading;

  // Detach: delete manifest records, keep source document. Internal helper —
  // `handleDetached` (which also refetches the grid) is the public surface.
  const detachManifestData = useCallback(async () => {
    if (!wasteManifestId) return;
    await deleteManifestMutation({ variables: { wasteManifestId } });
  }, [wasteManifestId, deleteManifestMutation]);

  // Detach manifest data and refresh the manifests list. Does not close the
  // parent dialog — the caller decides whether to close, advance to the next
  // doc in a multi-doc review, or stay put.
  const handleDetached = useCallback(async () => {
    await detachManifestData();

    if (wasteManifestId) onManifestChanged?.();

    client.refetchQueries({ include: MANIFEST_LIST_QUERIES });
  }, [detachManifestData, wasteManifestId, onManifestChanged, client]);

  // Delete the document and all associated manifest data. Does not close the
  // parent dialog (caller decides). Detach must run first due to the RESTRICT
  // FK on WasteManifest.sourceDocumentId. The two steps aren't transactional —
  // each failure mode surfaces a distinct alert so the user knows what state
  // the data is in.
  const handleDeleteDocument = useCallback(async () => {
    if (!documentId) return;
    try {
      await detachManifestData();
    } catch (err) {
      alerts.error(
        "Failed to detach manifest data; the document was not deleted.",
        err instanceof Error ? err : undefined
      );
      throw err;
    }
    try {
      await deleteDocumentMutation({
        variables: { id: documentId, retainAsset: false },
      });
    } catch (err) {
      alerts.error(
        "Manifest data was detached, but deleting the source document failed. Please retry from the document library.",
        err instanceof Error ? err : undefined
      );

      onManifestChanged?.();

      client.refetchQueries({ include: MANIFEST_LIST_QUERIES });
      throw err;
    }

    onManifestChanged?.();

    alerts.success("Document and associated manifest data deleted.");
    client.refetchQueries({ include: MANIFEST_LIST_QUERIES });
  }, [documentId, detachManifestData, deleteDocumentMutation, onManifestChanged, alerts, client]);

  const onDeleteWasteLine = useCallback(
    (lineNumber: string) => setDeletedWasteLineNumbers((prev) => [...prev, lineNumber]),
    []
  );
  const onDeleteTransporter = useCallback(
    (lineNumber: string) => setDeletedTransporterLineNumbers((prev) => [...prev, lineNumber]),
    []
  );

  /**
   * Memoized so the form can be.
   *
   * Every section of the review takes this object, and a new one each render
   * means none of them can skip a render they have no stake in — focus moving,
   * a validation pass landing. On a form this size that is a fifth of a second
   * of work each time, which is what the reviewer feels as the click not
   * registering.
   */
  return useMemo(
    () => ({
      manifestDataLoaded,
      manifestLoading,
      manifestSaving,
      deletingManifest,
      manifestNeedsReview,
      trackingNumberChanged,
      manifestControl,
      manifestWatch: watch,
      // Read without subscribing, for callers that run at event time rather than
      // during render — a keystroke handler wants the value as it is when the key
      // is pressed, and subscribing sixty fields to get it re-renders the form on
      // every character.
      manifestGetValues: getValues,
      manifestSetValue: setValue,
      manifestIssues,
      wasteLineFieldArray,
      transporterFieldArray,
      onDeleteWasteLine,
      onDeleteTransporter,
      manifestIsDirty,
      wasteManifestId,
      tenantId,
      deletingDocument,
      handleDetached,
      handleDeleteDocument,
      saveManifest,
      facility,
      suggestedFacility,
      suggestedFacilitySource,
      generatorSiteAddressInitiallyBlank,
      generatorMailingAddressPresent,
      readingPages,
      fieldConfidences,
      readingOffers,
    }),
    [
      manifestDataLoaded,
      manifestLoading,
      manifestSaving,
      deletingManifest,
      manifestNeedsReview,
      trackingNumberChanged,
      manifestControl,
      watch,
      getValues,
      setValue,
      manifestIssues,
      wasteLineFieldArray,
      transporterFieldArray,
      onDeleteWasteLine,
      onDeleteTransporter,
      manifestIsDirty,
      wasteManifestId,
      tenantId,
      deletingDocument,
      handleDetached,
      handleDeleteDocument,
      saveManifest,
      facility,
      suggestedFacility,
      suggestedFacilitySource,
      generatorSiteAddressInitiallyBlank,
      generatorMailingAddressPresent,
      readingPages,
      fieldConfidences,
      readingOffers,
    ]
  );
}
